<template>
  <!-- Quick View Modal -->
  <transition name="fade">
    <div v-if="isVisible" class="qv-root">
      <!-- backdrop -->
      <div class="qv-backdrop" aria-hidden="true" @click="closeModal"></div>

      <div class="qv-center fixed inset-0 flex items-center justify-center p-4 md:p-8 lg:p-16 overflow-y-auto">
        <div class="qv-panel glass w-full max-w-6xl max-h-[90vh] my-auto mt-10">
          <div class="qv-glow" aria-hidden="true"></div>
          <div class="qv-body">
            <div class="flex flex-col md:flex-row gap-6">
              <div class="w-full md:w-1/3">
                <div class="qv-media">
                  <img
                      v-if="currentImage"
                      :src="currentImage"
                      class="w-full h-64 object-contain"
                      alt="Product image"
                  >
                  <div v-else class="w-full h-64 flex items-center justify-center text-slate-400 text-[11px]">
                    بدون تصویر
                  </div>
                </div>

                <div class="flex flex-wrap gap-2 mt-3 max-h-20 overflow-y-auto pr-1 qv-scroll">
                  <!-- تصویر اصلی -->
                  <div
                      v-if="product.image"
                      class="qv-thumb"
                      @click="currentImage = img_source(product.image)"
                  >
                    <img :src="img_source(product.image)" class="w-full h-full object-cover" alt="thumb">
                  </div>

                  <!-- گالری -->
                  <div
                      v-for="(img, index) in product.gallery"
                      :key="index"
                      class="qv-thumb"
                      @click="currentImage = img.picture"
                  >
                    <img :src="img.picture" class="w-full h-full object-cover" alt="thumb">
                  </div>
                </div>
              </div>

              <!-- product info -->
              <div class="w-full md:w-2/3">
                <!-- header + close btn -->
                <div class="flex justify-between items-start gap-3">
                  <h3 class="max-w-[86%]">
                    <span
                        v-if="product.product_type === 'variation'"
                        class="block text-[12px] text-slate-400 leading-4 mb-1"
                    >
                      {{ product.variation_of_name }}
                    </span>

                    <span class="text-lg leading-6 font-semibold text-slate-100 break-words">
                      {{ product.name }}
                    </span>
                  </h3>

                  <button
                      @click="closeModal"
                      class="qv-close"
                      aria-label="Close"
                  >
                    <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>

                <!-- sku / status badges -->
                <div class="mt-3 flex flex-wrap gap-2">
                  <span class="qv-pill qv-pill-muted">
                    SKU: {{ product.sixteen_digit_code || '---' }}
                  </span>

                  <span
                      class="qv-pill font-medium"
                      :class="{
                      'qv-pill-green': product.status === 'publish',
                      'qv-pill-yellow': product.status === 'pending',
                      'qv-pill-muted': product.status === 'draft',
                      'qv-pill-red': product.status === 'archived'
                    }"
                  >
                    {{ getStatusLabel(product.status) }}
                  </span>

                  <span
                      class="qv-pill font-medium"
                      :class="{
                      'qv-pill-purple': product.product_type === 'simple',
                      'qv-pill-blue': product.product_type === 'variable',
                      'qv-pill-orange': product.product_type === 'variation'
                    }"
                  >
                    {{ getTypeLabel(product.product_type) }}
                  </span>

                  <span
                      class="qv-pill"
                      :class="{
                      'qv-pill-red': product.calculate_current_inventory === 0,
                      'qv-pill-blue': product.calculate_current_inventory > 0
                    }"
                  >
                    {{
                      product.calculate_current_inventory > 0
                          ? `موجودی: ${product.calculate_current_inventory} عدد`
                          : 'اتمام موجودی'
                    }}
                  </span>
                </div>

                <!-- price -->
                <div class="mt-4">
                  <div class="flex flex-wrap items-center gap-3">
                    <span class="text-xl font-extrabold text-slate-100 leading-none">
                      {{ product.price | toMoney }}
                      <span class="text-[11px] font-normal text-slate-400">تومان</span>
                    </span>

                    <span
                        v-if="product.regular_price > product.price"
                        class="text-sm text-slate-400 line-through"
                    >
                      {{ product.regular_price | toMoney }} تومان
                    </span>

                    <span
                        v-if="product.regular_price > product.price"
                        class="qv-pill qv-pill-pink font-medium"
                    >
                      {{ product.offer_percentage }} تخفیف
                    </span>
                  </div>
                </div>

                <div class="mt-4 grid grid-cols-2 gap-4 text-[12px] leading-5">
                  <div>
                    <p class="text-[11px] text-slate-400 mb-0.5">برند</p>
                    <p class="text-[13px] font-semibold text-slate-100 break-words" v-if="product.brand">
                      {{ product.brand.name || '---' }}
                    </p>
                    <p class="text-[13px] font-medium text-slate-500" v-else>---</p>
                  </div>

                  <div>
                    <p class="text-[11px] text-slate-400 mb-0.5">ارز</p>
                    <p class="text-[13px] font-semibold text-slate-100">
                      {{ product.currency_name || '---' }}
                    </p>
                  </div>
                </div>

                <!-- categorization / avails / feature as 3 cols -->
                <div class="mt-4 qv-box">
                  <div class="grid grid-cols-1 md:grid-cols-3 gap-3">

                    <!-- دسته‌بندی‌ها -->
                    <div>
                      <p class="text-[11px] text-slate-400 mb-1">دسته‌بندی</p>
                      <div class="flex flex-wrap gap-1">
                        <span
                            v-for="(cat, i) in product.categorization"
                            :key="'c'+i"
                            class="qv-tag qv-tag-indigo"
                        >
                          {{ cat.name }}
                        </span>
                        <span v-if="!product.categorization || !product.categorization.length" class="text-[11px] text-slate-500">
                          -
                        </span>
                      </div>
                    </div>

                    <!-- خواص -->
                    <div>
                      <p class="text-[11px] text-slate-400 mb-1">خواص / مزایا</p>
                      <div class="flex flex-wrap gap-1">
                        <span
                            v-for="(a, i) in product.avails"
                            :key="'a'+i"
                            class="qv-tag qv-tag-emerald"
                        >
                          {{ a.name }}
                        </span>
                        <span v-if="!product.avails || !product.avails.length" class="text-[11px] text-slate-500">
                          -
                        </span>
                      </div>
                    </div>

                    <!-- ویژگی‌ها -->
                    <div>
                      <p class="text-[11px] text-slate-400 mb-1">ویژگی‌ها</p>
                      <div class="flex flex-wrap gap-1">
                        <span
                            v-for="(f, i) in product.feature"
                            :key="'f'+i"
                            class="qv-tag qv-tag-purple"
                        >
                          {{ f.name }}
                        </span>
                        <span v-if="!product.feature || !product.feature.length" class="text-[11px] text-slate-500">
                          -
                        </span>
                      </div>
                    </div>

                  </div>
                </div>

                <div class="mt-6">

                  <div class="flex flex-wrap gap-2 border-b border-white/10 pb-2">
                    <button
                        v-for="tab in tabs"
                        :key="tab.key"
                        @click="activeTab = tab.key"
                        class="relative px-3 py-1.5 text-[11px] font-medium rounded-md transition-all duration-200 focus:outline-none"
                        :class="activeTab === tab.key
                        ? 'qv-tab-active'
                        : 'qv-tab'"
                    >
                      {{ tab.label }}

                      <span
                          v-if="activeTab === tab.key"
                          class="absolute -bottom-[9px] left-0 right-0 mx-auto w-10 h-[3px] rounded-full qv-tab-underline"
                      ></span>
                    </button>
                  </div>

                  <div class="mt-4 qv-editor qv-scroll">
                    <!-- توضیحات کوتاه -->
                    <div v-show="activeTab === 'summary_explanation'">
                      <quill-editor
                          disabled="true"
                          :options="bubbleReadOnlyOptions"
                          v-model="product.summary_explanation"
                          class="ql-readonly white-text"
                      />
                      <div
                          v-if="!cleanHasContent(product.summary_explanation)"
                          class="text-[11px] text-slate-400 text-center py-6"
                      >
                        توضیحات کوتاه ثبت نشده 📝
                      </div>
                    </div>

                    <!-- نحوه مصرف -->
                    <div v-show="activeTab === 'how_to_use'">
                      <quill-editor
                          disabled="true"
                          :options="bubbleReadOnlyOptions"
                          v-model="product.how_to_use"
                          class="ql-readonly"
                      />
                      <div
                          v-if="!cleanHasContent(product.how_to_use)"
                          class="text-[11px] text-slate-400 text-center py-6"
                      >
                        نحوه مصرف ثبت نشده 🍯
                      </div>
                    </div>

                    <!-- سوالات متداول -->
                    <div v-show="activeTab === 'faq'">
                      <quill-editor
                          disabled="true"
                          :options="bubbleReadOnlyOptions"
                          v-model="product.faq"
                          class="ql-readonly"
                      />
                      <div
                          v-if="!cleanHasContent(product.faq)"
                          class="text-[11px] text-slate-400 text-center py-6"
                      >
                        سوالات متداول ثبت نشده ❓
                      </div>
                    </div>

                    <!-- بلاگ -->
                    <div v-show="activeTab === 'blog'">
                      <quill-editor
                          disabled="true"
                          :options="bubbleReadOnlyOptions"
                          v-model="product.blog"
                          class="ql-readonly"
                      />
                      <div
                          v-if="!cleanHasContent(product.blog)"
                          class="text-[11px] text-slate-400 text-center py-6"
                      >
                        محتوای بلاگ ثبت نشده ✍️
                      </div>
                    </div>
                  </div>
                </div>
                <!-- /TABS -->

              </div>
            </div>

            <!-- variations table (only for variable products) -->
            <div
                class="qv-table mt-6"
                v-if="product.product_type === 'variable'"
            >
              <table class="min-w-full divide-y divide-white/10 text-center">
                <thead class="bg-slate-950/40">
                <tr class="text-center">
                  <th scope="col" class="px-4 py-3 text-[10px] font-medium text-slate-300 uppercase tracking-wider">
                    تصویر
                  </th>
                  <th scope="col" class="px-4 py-3 text-[10px] font-medium text-slate-300 uppercase tracking-wider">
                    نام
                  </th>
                  <th scope="col" class="px-4 py-3 text-[10px] font-medium text-slate-300 uppercase tracking-wider">
                    SKU
                  </th>
                  <th scope="col" class="px-4 py-3 text-[10px] font-medium text-slate-300 uppercase tracking-wider">
                    موجودی
                  </th>
                  <th scope="col" class="px-4 py-3 text-[10px] font-medium text-slate-300 uppercase tracking-wider">
                    قیمت
                  </th>
                </tr>
                </thead>

                <tbody class="divide-y divide-white/10">
                <tr
                    v-for="(variation, index) in product.variations"
                    v-if="variation.active ==true"
                    :key="index"
                    class="hover:bg-white/5"
                >
                  <td class="px-4 py-4 whitespace-nowrap text-center">
                    <div
                        v-if="variation.image"
                        class="mx-auto w-10 h-10 rounded-md overflow-hidden border border-white/10"
                    >
                      <img
                          :src="img_source(variation.image)"
                          class="w-full h-full object-cover"
                          alt="Variation image"
                      >
                    </div>
                    <div
                        v-else
                        class="w-10 h-10 flex items-center justify-center text-[10px] text-slate-400 border border-white/10 rounded-md mx-auto"
                    >
                      -
                    </div>
                  </td>

                  <td class="px-4 py-4 whitespace-nowrap text-[12px] text-slate-200 text-center">
                    {{ variation.name }}
                  </td>

                  <td class="px-4 py-4 whitespace-nowrap text-[12px] text-slate-200 font-mono text-center">
                    {{ variation.sixteen_digit_code }}
                  </td>

                  <td class="px-4 py-4 whitespace-nowrap text-center">
                      <span
                          class="qv-pill font-medium"
                          :class="{
                          'qv-pill-green': variation.calculate_current_inventory > 10,
                          'qv-pill-yellow': variation.calculate_current_inventory <= 10 && variation.calculate_current_inventory > 0,
                          'qv-pill-red': variation.calculate_current_inventory <= 0
                        }"
                      >
                        {{
                          variation.calculate_current_inventory > 0
                              ? `${variation.calculate_current_inventory} در انبار`
                              : 'ناموجود'
                        }}
                      </span>
                  </td>

                  <td class="px-4 py-4 whitespace-nowrap text-center">
                    <div class="text-[11px] font-semibold text-slate-100">
                      {{ variation.price | toMoney }} تومان
                    </div>
                    <div
                        v-if="variation.regular_price > variation.price"
                        class="text-[10px] text-emerald-300 line-through mt-1"
                    >
                      {{ variation.regular_price | toMoney }} تومان
                    </div>
                  </td>
                </tr>
                </tbody>
              </table>

              <div
                  v-if="!product.variations || !product.variations.length"
                  class="text-center py-8 text-slate-400"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto text-slate-500" fill="none"
                     viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"/>
                </svg>
                <p class="mt-2 text-[12px]">هنوز متغیری اضافه نشده است</p>
              </div>
            </div>
            <!-- /variations table -->

          </div>

          <!-- footer actions -->
          <div class="qv-footer">
            <button
                type="button"
                class="qv-fab"
                @click="editProduct"
                title="ویرایش محصول"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="white">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
              </svg>
            </button>
          </div>

        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { quillEditor } from 'vue-quill-editor'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'

export default {
  name: 'ProductQuickView',
  components: { quillEditor },
  props: {
    product: {
      type: Object,
      required: true
    },
    isVisible: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      // تب های توضیحات
      tabs: [
        { key: 'summary_explanation', label: 'توضیحات کوتاه' },
        { key: 'how_to_use',          label: 'نحوه مصرف' },
        { key: 'faq',                 label: 'سوالات متداول' },
        { key: 'blog',                label: 'بلاگ / نکات' },
      ],
      activeTab: 'summary_explanation',

      // ادیتور readonly تمیز
      bubbleReadOnlyOptions: {
        readOnly: true,
        theme: 'bubble',
        modules: { toolbar: false },
      },

      // فقط برای fallback قبلی
      editorOptions: {
        placeholder: 'توضیحی ثبت نشده',
        theme: 'bubble',
      },

      currentImage: null,
    }
  },
  watch: {
    product(newVal) {
      if (newVal && newVal.image) {
        this.currentImage = this.img_source(newVal.image)
      } else {
        this.currentImage = null
      }
    },
    isVisible(newVal) {
      if (newVal) {
        document.body.classList.add('modal-open')
      } else {
        document.body.classList.remove('modal-open')
      }
    }

  },
  beforeDestroy() {
    document.body.classList.remove('modal-open')
  },
  created() {
    if (this.product && this.product.image) {
      this.currentImage = this.img_source(this.product.image)
    }
  },
  methods: {
    closeModal() {
      this.currentImage = null
      this.$emit('close')
    },
    editProduct() {
      this.$emit('edit', this.product)
      this.closeModal()
    },
    getStatusLabel(status) {
      const statusMap = {
        'publish': 'منتشر شده',
        'pending': 'در انتظار',
        'draft': 'پیش نویس',
        'archived': 'آرشیو شده',
      }
      return statusMap[status] || status || '---'
    },
    getTypeLabel(type) {
      const typeMap = {
        'simple': 'بدون متغیر',
        'variable': 'دارای متغیر',
        'variation': 'متغیر',
      }
      return typeMap[type] || type || '---'
    },

    // محتوا واقعاً چیزی داره یا فقط <p><br></p>؟
    cleanHasContent(html) {
      if (!html) return false
      const stripped = String(html)
          .replace(/<p><br><\/p>/gi, '')
          .replace(/<br\s*\/?>/gi, '')
          .replace(/<\/?p[^>]*>/gi, '')
          .replace(/&nbsp;/gi, '')
          .trim()
      return stripped.length > 0
    },
  },
  filters: {
    toMoney(v) {
      const n = Number(v || 0)
      return n.toLocaleString('fa-IR')
    }
  }
}
</script>

<style scoped>
/* =======================
   Centered modal shell
   ======================= */
.fade-enter-active,
.fade-leave-active { transition: opacity 0.22s ease; }
.fade-enter,
.fade-leave-to { opacity: 0; }

.qv-root{
  position: fixed;
  inset: 0;
  z-index: 9999;
}

.qv-backdrop{
  position: absolute;
  inset: 0;
  background: rgba(2, 6, 23, 0.84);
  backdrop-filter: blur(18px) saturate(140%);
  -webkit-backdrop-filter: blur(18px) saturate(140%);
}


.qv-panel{
  position: relative;
  width: min(1120px, 100%);
  max-height: calc(100vh - 32px);
  overflow: hidden;
  border-radius: 18px;
}

/* =======================
   Dark Glass + Glow
   ======================= */
.glass{
  background: rgba(2, 6, 23, 0.58);
  border: 1px solid rgba(255,255,255,0.10);
  box-shadow:
      0 28px 90px rgba(0,0,0,0.62),
      inset 0 1px 0 rgba(255,255,255,0.06);
  backdrop-filter: blur(22px) saturate(140%);
  -webkit-backdrop-filter: blur(22px) saturate(140%);
}

.qv-glow{
  position: absolute;
  inset: -140px;
  pointer-events: none;
  filter: blur(90px);
  opacity: 0.60;
  background:
      radial-gradient(circle at 18% 18%, rgba(99,102,241,0.18), transparent 58%),
      radial-gradient(circle at 78% 65%, rgba(168,85,247,0.22), transparent 60%),
      radial-gradient(circle at 55% 35%, rgba(236,72,153,0.18), transparent 58%),
      radial-gradient(circle at 40% 85%, rgba(249,115,22,0.14), transparent 60%);
}

.qv-body{
  position: relative;
  z-index: 1;
  padding: 18px;
  overflow: auto;
  max-height: calc(100vh - 32px - 58px);
}

.qv-footer{
  position: relative;
  z-index: 1;
  background: rgba(15,23,42,0.45);
  border-top: 1px solid rgba(255,255,255,0.10);
  padding: 10px 14px;
  display: flex;
  justify-content: flex-end;
}

/* close button */
.qv-close{
  color: rgba(148,163,184,0.95);
  transition: color .15s ease, transform .15s ease;
}
.qv-close:hover{
  color: rgba(226,232,240,1);
  transform: rotate(6deg);
}

/* media / thumbs */
.qv-media{
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.qv-thumb{
  width: 48px;
  height: 48px;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid rgba(255,255,255,0.10);
  background: rgba(255,255,255,0.04);
  transition: border-color .15s ease, transform .15s ease;
}
.qv-thumb:hover{
  border-color: rgba(236,72,153,0.35);
  transform: translateY(-1px);
}

/* pills */
.qv-pill{
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 10px;
  border: 1px solid rgba(255,255,255,0.10);
  background: rgba(255,255,255,0.05);
  color: rgba(226,232,240,0.95);
}
.qv-pill-muted { background: rgba(148,163,184,0.10); color: rgba(226,232,240,0.95); }
.qv-pill-green { background: rgba(16,185,129,0.14); color: rgba(110,231,183,1); }
.qv-pill-yellow{ background: rgba(234,179,8,0.14); color: rgba(253,224,71,1); }
.qv-pill-red   { background: rgba(239,68,68,0.14); color: rgba(252,165,165,1); }
.qv-pill-blue  { background: rgba(59,130,246,0.14); color: rgba(147,197,253,1); }
.qv-pill-purple{ background: rgba(168,85,247,0.14); color: rgba(216,180,254,1); }
.qv-pill-orange{ background: rgba(249,115,22,0.14); color: rgba(253,186,116,1); }
.qv-pill-pink  { background: rgba(236,72,153,0.16); color: rgba(251,113,133,1); }

/* box section */
.qv-box{
  background: rgba(15,23,42,0.40);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 12px;
  padding: 12px;
}

/* tags */
.qv-tag{
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 10px;
  border: 1px solid rgba(255,255,255,0.10);
  background: rgba(255,255,255,0.05);
  color: rgba(226,232,240,0.95);
}
.qv-tag-indigo{ background: rgba(99,102,241,0.14); color: rgba(199,210,254,1); border-color: rgba(99,102,241,0.25); }
.qv-tag-emerald{ background: rgba(16,185,129,0.14); color: rgba(167,243,208,1); border-color: rgba(16,185,129,0.25); }
.qv-tag-purple{ background: rgba(168,85,247,0.14); color: rgba(216,180,254,1); border-color: rgba(168,85,247,0.25); }

/* tabs */
.qv-tab{
  background: rgba(255,255,255,0.06);
  color: rgba(148,163,184,1);
}
.qv-tab:hover{
  background: rgba(255,255,255,0.08);
  color: rgba(226,232,240,1);
}
.qv-tab-active{
  background-image: linear-gradient(90deg, rgba(168,85,247,0.70), rgba(236,72,153,0.60), rgba(249,115,22,0.55));
  color: white;
  box-shadow: 0 10px 30px rgba(0,0,0,0.35);
}
.qv-tab-underline{
  background-image: linear-gradient(90deg, rgba(168,85,247,0.80), rgba(236,72,153,0.70), rgba(249,115,22,0.65));
  box-shadow: 0 8px 18px rgba(236,72,153,0.22);
}

/* editor panel */
.qv-editor{
  background: rgba(2, 6, 23, 0.35);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 14px;
  padding: 12px;
  max-height: 230px;
  overflow: auto;
}

/* table wrapper */
.qv-table{
  background: rgba(2, 6, 23, 0.35);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 14px;
  overflow: hidden;
}

/* footer action button */
.qv-fab{
  padding: 8px;
  border-radius: 999px;
  background-image: linear-gradient(90deg, rgba(168,85,247,0.90), rgba(236,72,153,0.85), rgba(249,115,22,0.75));
  box-shadow: 0 16px 40px rgba(0,0,0,0.45);
  transition: transform .15s ease, filter .15s ease;
}
.qv-fab:hover{
  transform: translateY(-1px) scale(1.03);
  filter: brightness(1.05);
}

/* scrollbar */
.qv-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
.qv-scroll::-webkit-scrollbar-track { background: rgba(255,255,255,0.05); border-radius: 999px; }
.qv-scroll::-webkit-scrollbar-thumb {
  background: linear-gradient(to bottom, rgba(168,85,247,0.65), rgba(236,72,153,0.55));
  border-radius: 999px;
}

/* =======================
   Quill readonly styling
   ======================= */
.ql-readonly {
  background-color: transparent;
  border: none;
  padding: 0;
  min-height: auto;
  color: white;
}
.ql-readonly .ql-editor {
  font-size: 12px;
  line-height: 1.9;
  color: rgba(226,232,240,0.95);
  padding: 0;
  text-align: right;
  word-break: break-word;
}
.ql-container.ql-bubble {
  border: none !important;
  box-shadow: none !important;
}
</style>
